import { Link } from 'react-router-dom'
import { purchasesAllowed } from '../../utils/platform.js'

/**
 * Inline upgrade prompt for TIER_LIMIT errors.
 *
 * API enforcement returns HTTP 402 with:
 *   { error: { code: 'TIER_LIMIT', message, meta: { tier, limit, current, kind } } }
 *
 * Components catch the error, and when err.code === 'TIER_LIMIT' they
 * render this instead of a generic toast. The meta.kind hint lets us
 * suggest the most relevant plan (person_cap + doc_scan_cap → Plus;
 * collaborator_cap + invite_gate + origin_ai_quota → Researcher).
 *
 * Usage:
 *   <UpgradeInline error={err} />
 *   <UpgradeInline message="Manual override" suggestedPlan="plus" />
 */
const PLAN_FOR_KIND = {
  person_cap: 'plus',
  gedcom_import_cap: 'plus',
  doc_scan_cap: 'plus',
  invite_gate: 'researcher',
  collaborator_cap: 'researcher',
  origin_ai_quota: 'researcher',
}

export default function UpgradeInline({ error, message, suggestedPlan, className = '' }) {
  // Only render for TIER_LIMIT errors when `error` is provided; otherwise
  // treat the component as explicit CTA driven by `message`.
  if (error && error.code !== 'TIER_LIMIT') return null

  const text = message || error?.message || 'This action requires a paid plan.'
  const kind = error?.meta?.kind
  const plan = suggestedPlan || PLAN_FOR_KIND[kind] || 'plus'
  const upgradeHref = `/billing?plan=${plan}`
  const planLabel = plan === 'researcher' ? 'Researcher' : 'Plus'

  return (
    <div className={`upgrade-inline ${className}`} role="alert">
      <div className="upgrade-inline__body">
        <div className="upgrade-inline__message">{text}</div>
      </div>
      {purchasesAllowed() ? (
        <Link to={upgradeHref} className="btn btn-primary btn-sm upgrade-inline__cta">
          Upgrade to {planLabel}
        </Link>
      ) : (
        // Native (Apple 3.1.1): no in-app purchase or external purchase link for
        // digital goods. Plain note pointing to the web — no tappable buy link.
        <div className="upgrade-inline__note">
          {planLabel} feature — manage your plan at <strong>genealogic.ai</strong>.
        </div>
      )}
    </div>
  )
}
